import { computed, unref } from 'vue'
import { useHead } from '@unhead/vue'
import { useSanitizeHtml } from './useSanitizeHtml'
import { BASE_URL, DEFAULT_IMAGE, SITE_NAME } from '@/constants/seo'

const { stripHtml } = useSanitizeHtml()

export function useSeo(options) {
  const title = computed(() => unref(options.title))
  const description = computed(() => unref(options.description))
  const canonical = computed(() => {
    const path = unref(options.canonical) || ''
    return path.startsWith('http') ? path : `${BASE_URL}${path}`
  })
  const image = computed(() => unref(options.image) || DEFAULT_IMAGE)
  const type = options.type || 'website'
  const robots = computed(() => unref(options.robots))
  const article = computed(() => unref(options.article))

  useHead({
    title,
    meta: () => [
      { name: 'description', content: description.value },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: title.value },
      { property: 'og:description', content: description.value },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: image.value },
      { property: 'og:locale', content: 'es_ES' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title.value },
      { name: 'twitter:description', content: description.value },
      { name: 'twitter:image', content: image.value },
      ...(robots.value ? [{ name: 'robots', content: robots.value }] : []),
      ...(type === 'article' && article.value ? [
        { property: 'article:published_time', content: article.value.publishedAt },
        { property: 'article:modified_time', content: article.value.updatedAt },
        { property: 'article:author', content: article.value.author }
      ].filter(tag => tag.content) : [])
    ],
    link: [
      { rel: 'canonical', href: canonical }
    ]
  })
}

/**
 * Genera meta description desde contenido HTML
 * @param {string} content - Contenido HTML o texto
 * @param {number} maxLength - Longitud máxima (default 155)
 * @returns {string} Description truncada
 */
export function generateDescription(content, maxLength = 155) {
  if (!content) return ''
  const text = stripHtml(content)
  if (text.length <= maxLength) return text
  const truncated = text.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...'
}

export default useSeo
